import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound, MailCheck } from 'lucide-react';

/**
 * Post-signup email verification screen. Reached from:
 *   1. RegisterPage after a public main signup returns `verification_required`
 *   2. LoginPage when the backend rejects with `EMAIL_NOT_VERIFIED`
 * Both flows pass the target address via `?email=…` so the user only has
 * to type the 6-digit code.
 */
const VerifyEmailPage = () => {
  const { verifyEmail, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Please enter your email');
    if (code.length !== 6) return toast.error('Please enter the 6-digit code we sent you');

    setLoading(true);
    try {
      await verifyEmail(email.trim().toLowerCase(), code.trim());
      toast.success('Email confirmed — welcome to FleetCost.App!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return toast.error('Enter your email first so we know where to send it');
    setResending(true);
    try {
      await resendVerification(email.trim().toLowerCase());
      toast.success('If that email is unverified, a fresh code is on its way');
    } catch {
      toast.error("Couldn't resend right now — please try again in a minute");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="/fleetcost-logo.png"
            alt="FleetCost.App"
            className="h-16 mx-auto object-contain mb-2"
            data-testid="verify-email-logo"
          />
          <p className="text-slate-500 text-sm mt-1">Confirm your email to activate your account</p>
        </div>

        <Card className="shadow-lg border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <MailCheck className="w-5 h-5 text-emerald-600" /> Check your inbox
            </CardTitle>
            <CardDescription>
              We sent a 6-digit code to <strong className="text-slate-700">{email || 'your email address'}</strong>.
              The code expires in 24&nbsp;hours. Not in your inbox? Check your <strong>spam / junk</strong> folder,
              or use “Resend code” below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="verify-email-input">Email</Label>
                <Input
                  id="verify-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.co.za"
                  disabled={loading}
                  data-testid="verify-email-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="verify-code-input">6-digit code</Label>
                <Input
                  id="verify-code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="tracking-[0.5em] text-center font-mono text-lg"
                  disabled={loading}
                  data-testid="verify-code-input"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full"
                data-testid="verify-submit-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                {loading ? 'Confirming…' : 'Confirm & Sign In'}
              </Button>
            </form>

            <div className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResend}
                disabled={resending}
                data-testid="verify-resend-btn"
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                {resending ? 'Sending…' : "Didn't get it? Resend code"}
              </Button>
              <Link
                to="/login"
                className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
                data-testid="verify-back-to-login"
              >
                <ArrowLeft className="w-3 h-3" /> Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
